import { useMonth } from "../../hooks/useMonth";
import { monthSlugsWithOverview } from "../../services/centralStore";
import dayjs from "dayjs";
import { capitalizeFirstLetter } from "normalize-text";

type AfData = {
    airCode?: string;
    ebId?: number;
    vtUrl?: string;
    vtId?: number;
    expResort?: string;
};

type ResortForAfils = {
    slug: string;
    name: string;
    region: {
        slug: string;
        country: {
            slug: string;
        };
    };
};

type RegionForAfils = {
    slug: string;
    slugName: string;
    name: string;
    isCountry: boolean;
    country: {
        slug: string;
    };
};

const expBaseUrl =
    "https://www.expedia.com/Hotel-Search?destination=PLACE&startDate=STARTDATE&endDate=ENDDATE&adults=2&rooms=1&theme=&userIntent=&semdtl=&useRewards=false&sort=RECOMMENDED";
const exp = `https://expedia.com/affiliate?siteid=1&landingPage=URL&camref=1011l3UnaQ&creativeref=1100l68075`;

const viatorBaseUrl = "https://www.viator.comURLPART";
const viator =
    "https://tp.media/r?marker=564086.SUBID&trs=340797&p=1922&u=URL&campaign_id=47";

const ebBaseUrl =
    "https://www.economybookings.com/en/cars/results?lang=en&age=35&py=YEAR1&pm=MONTH1&pd=DAY1&dy=YEAR2&dm=MONTH2&dd=DAY2&pt=1000&dt=1000&plc=PLACE1&dlc=PLACE2&reload=1";
const eb =
    "https://tp.media/r?marker=564086.SUBID&trs=340797&p=2018&u=URL&campaign_id=10";
const kiwiBaseUrl =
    "https://www.kiwi.com/deep?to=DESTINATION&departure=STARTDATE&return=ENDDATE";
const kiwi =
    "https://tp.media/click?shmarker=564086.SUBID&promo_id=3791&source_type=customlink&type=click&campaign_id=111&trs=340797&custom_url=URL";

const kiwiDefault = "https://www.kiwi.com";
const viatorDefault = "https://www.viator.com";
const ebDefault = "https://www.economybookings.com";

const bkngBaseUrl =
    "https://www.booking.com/searchresults.en-gb.html?ss=PLACE&checkin=STARTDATE&checkout=ENDDATE&group_adults=2&no_rooms=1&group_children=0";

const bkng =
    "https://tp.media/r?marker=564086.SUBID&trs=340797&p=2076&u=URL&campaign_id=84";

export const useAfilsUrls = ({
    afData,
    region,
    resort,
    resultMonthIndex,
    subId
}: {
    afData: AfData | undefined;
    region?: RegionForAfils;
    resort?: ResortForAfils;
    resultMonthIndex?: number;
    subId?: string;
}) => {
    const { month } = useMonth({ ignoreOverview: true });
    const monthIndex =
        resultMonthIndex ?? monthSlugsWithOverview.indexOf(month);
    let firstDate = dayjs().month(monthIndex).startOf("month");
    if (firstDate.isBefore(dayjs())) {
        firstDate = firstDate.add(1, "year");
    }
    let startDate = firstDate.endOf("week").add(7, "days");
    let endDate = startDate.add(7, "days");
    const startDateStr = startDate.format("YYYY-MM-DD");
    const endDateStr = endDate.format("YYYY-MM-DD");

    // EXPEDIA, BOOKING
    let name = resort ? resort.name : region.slugName || region.name;
    if (region) {
        if (afData?.expResort) {
            name = afData.expResort;
        }
    }
    const ensuredRegion = region || resort.region;
    const country = ensuredRegion.country.slug;
    const finalName = name.replace(/-/g, " ");
    const bkngBaseUrlReplaced = encodeURIComponent(
        bkngBaseUrl
            .replace("PLACE", finalName)
            .replace("STARTDATE", startDateStr)
            .replace("ENDDATE", endDateStr)
    );
    const bkngUrl = bkng
        .replace("URL", bkngBaseUrlReplaced)
        .replace("SUBID", subId || "1");

    const expName = name
        .split(" ")
        .map((r) => capitalizeFirstLetter(r))
        .join(" ");

    // KIWI
    let kiwiUrlReplaced = kiwiDefault;
    if (afData?.airCode) {
        kiwiUrlReplaced = kiwiBaseUrl
            .replace("STARTDATE", startDateStr)
            .replace("ENDDATE", endDateStr)
            .replace("DESTINATION", afData.airCode);
    }
    const kiwiUrl = kiwi
        .replace("URL", encodeURIComponent(kiwiUrlReplaced))
        .replace("SUBID", subId || "1");

    // VIATOR
    const vtBase = afData?.vtUrl;
    const { viatorUrl, viatorName } = getVtUrl(vtBase, subId);
    

    // ECONOMYBOOKINGS
    let ebBaseUrlReplaced = ebDefault;
    if (afData?.ebId) {
        ebBaseUrlReplaced = ebBaseUrl
            .replace("YEAR1", startDate.format("YYYY"))
            .replace("MONTH1", startDate.format("MM"))
            .replace("DAY1", startDate.format("DD"))
            .replace("YEAR2", endDate.format("YYYY"))
            .replace("MONTH2", endDate.format("MM"))
            .replace("DAY2", endDate.format("DD"))
            .replace("PLACE1", afData.ebId.toString())
            .replace("PLACE2", afData.ebId.toString());
    }
    const ebUrl = eb
        .replace("URL", encodeURIComponent(ebBaseUrlReplaced))
        .replace("SUBID", subId || "1");

    return { kiwiUrl, viatorUrl, expUrl: bkngUrl, ebUrl, viatorName, expName };
};

export const getVtUrl = (vtBase, subId) => {
    let viatorName = undefined;
    let viatorUrlReplaced = viatorDefault;
    if (vtBase) {
        const mainPart = vtBase?.split("/")[1];
        viatorUrlReplaced = viatorBaseUrl.replace("URLPART", vtBase);

        viatorName = mainPart
            .split("-")
            .map((r) => capitalizeFirstLetter(r))
            .join(" ");
    }
    const viatorUrl = viator
        .replace("URL", encodeURIComponent(viatorUrlReplaced))
        .replace("SUBID", subId || "1");
    return { viatorUrl, viatorName };
}
